import { useState } from 'react';
import { Pagination, Navigation, HashNavigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import shieldAssets from './shieldAssets';
import tshirtdUtils from './tshirtAssets';

import { ShieldTeam } from '@/components/ShieldTeam';
import { Svg } from '@/types/editProfile';

type SelectorType =
  | 'shieldModel'
  | 'shieldPattern'
  | 'tshirtNeck'
  | 'tshirtPattern'
  | 'tshirtSponsor'
  | ' tshirtBackground'
  | 'tshirtModel';

type OptionSelectorProps = {
  initialOption: string | null;
  label: string;
  type: SelectorType;
  handleCustomizationValuesChange: (values: Svg) => void;
  values?: Svg;
};

export const OptionSelector = ({
  label,
  type,
  values,
  initialOption,
  handleCustomizationValuesChange,
}: OptionSelectorProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(
    initialOption
  );

  const handleOptionSelect = (optionId: number) => {
    let updatedValues = { ...values };
    if (selectedOption === String(optionId)) {
      return;
    }

    if (type === 'shieldModel') {
      updatedValues = {
        ...values,
        escudo: { ...values?.escudo, escudoparam1: String(optionId) },
      };
    } else if (type === 'shieldPattern') {
      updatedValues = {
        ...values,
        escudo: { ...values?.escudo, escudoparam2: String(optionId) },
      };
    } else if (type === 'tshirtPattern') {
      updatedValues = {
        ...values,
        camiseta: { ...values?.camiseta, param2: String(optionId) },
      };
    }

    setSelectedOption(
      selectedOption === String(optionId) ? null : String(optionId)
    );
    handleCustomizationValuesChange(updatedValues);
  };

  const renderShieldModelSlides = () => {
    return shieldAssets.shieldModelSlides.map((slideOptions, slideIndex) => (
      <SwiperSlide key={slideIndex}>
        <div className="flex items-center pt-0 lg:pt-2 pb-12 justify-between">
          {slideOptions.map((option) => (
            <div
              key={option.id}
              className="relative flex justify-center items-center min-w-[62px] h-[62px] bg-[#F8F8F8] rounded-lg cursor-pointer"
              onClick={() => handleOptionSelect(parseInt(option.id))}
            >
              <ShieldTeam
                backgroundColor={option.backgroundColor}
                className="w-[28.94px] h-[32.75px]"
                id={option.shieldId}
                shieldBase={option.shieldBase}
              />
              {renderSelectButton(option.id)}
            </div>
          ))}
        </div>
      </SwiperSlide>
    ));
  };

  const renderShieldPatternSlides = () => {
    return shieldAssets.shieldPatternsSlides.map((slideOptions, slideIndex) => (
      <SwiperSlide key={slideIndex}>
        <div className="flex items-center pt-0 lg:pt-2 pb-12 justify-between">
          {slideOptions.map((option) => (
            <div
              key={option.id}
              className="relative flex justify-center items-center min-w-[62px] h-[62px] bg-[#F8F8F8] rounded-lg cursor-pointer"
              onClick={() => handleOptionSelect(parseInt(option.id))}
            >
              <svg
                className="w-[28.94px] h-[32.75px]"
                fill="#A09EB3"
                viewBox="0 0 29 33"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d={option.pattern} />
              </svg>
              {renderSelectButton(option.id)}
            </div>
          ))}
        </div>
      </SwiperSlide>
    ));
  };

  const renderTshirtPatternSlides = () => {
    return tshirtdUtils.tshirtModelSlides.map((slideOptions, slideIndex) => (
      <SwiperSlide key={slideIndex}>
        <div className="space-x-4 flex items-center pt-0 lg:pt-2 pb-12 justify-start">
          {slideOptions.map((option) => (
            <div
              key={option.id}
              className="relative flex justify-center items-center w-[62px] h-[62px] bg-[#F8F8F8] rounded-lg cursor-pointer"
              onClick={() => handleOptionSelect(option.id)}
            >
              <svg
                className="w-[33px]"
                fill="#A09EB3"
                viewBox="0 0 45.75 40.37"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d={option.path} />
              </svg>
              {renderSelectButton(String(option.id))}
            </div>
          ))}
        </div>
      </SwiperSlide>
    ));
  };

  const renderSelectButton = (optionId: string) => (
    <div className="absolute bottom-2 right-2 w-4 h-4 rounded-full bg-white border border-gdt-blue flex items-center justify-center cursor-pointer">
      {selectedOption === optionId && (
        <div className="w-3 h-3 rounded-full bg-blue-500 flex items-center justify-center">
          <svg
            className="w-2 h-2 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M5 13l4 4L19 7"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
            />
          </svg>
        </div>
      )}
    </div>
  );

  const renderSlides = () => {
    switch (type) {
      case 'shieldModel':
        return renderShieldModelSlides();
      case 'shieldPattern':
        return renderShieldPatternSlides();
      case 'tshirtPattern':
        return renderTshirtPatternSlides();
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="font-bold">{label}</div>
      <div className="w-full max-w-xs lg:max-w-3xl mx-auto h-[105px] overflow-hidden mt-3 lg:mt-0">
        <Swiper
          className="[&_.swiper-button-prev]:!text-white 
            [&_.swiper-button-next]:!text-white
            [&_.swiper-button-prev]:!bg-blue-600 
            [&_.swiper-button-next]:!bg-blue-600
            [&_.swiper-button-prev:hover]:!bg-blue-700 
            [&_.swiper-button-next:hover]:!bg-blue-700
            [&_.swiper-button-prev]:!rounded-lg 
            [&_.swiper-button-next]:!rounded-lg
            [&_.swiper-button-prev]:!w-[24px] 
            [&_.swiper-button-next]:!w-[24px]
            [&_.swiper-button-prev]:!h-[24px] 
            [&_.swiper-button-next]:!h-[24px]
            [&_.swiper-button-prev]:!left-2 
            [&_.swiper-button-next]:!right-2
            [&_.swiper-button-prev::after]:!text-lg 
            [&_.swiper-button-next::after]:!text-lg
            [&_.swiper-button-disabled]:!bg-gdt-gray-6 
            [&_.swiper-button-disabled]:!text-gray-400
            [&_.swiper-button-prev::after]:!content-[''] 
            [&_.swiper-button-next::after]:!content-['']
            [&_.swiper-pagination]:!flex [&_.swiper-pagination]:!justify-center
            [&_.swiper-pagination]:!gap-2 [&_.swiper-pagination]:!bottom-14
            !relative"
          hashNavigation={{
            watchState: true,
          }}
          modules={[Pagination, Navigation, HashNavigation]}
          navigation={{
            prevEl: '.swiper-button-prev',
            nextEl: '.swiper-button-next',
          }}
          pagination={{
            clickable: true,
            bulletClass:
              'swiper-pagination-bullet bg-gray-300 w-2 h-2 mx-1 opacity-100',
            bulletActiveClass:
              'swiper-pagination-bullet-active bg-blue-600 w-4 rounded-lg opacity-100',
          }}
        >
          <div className="swiper-button-prev absolute  z-50 top-[100px]">
            <svg
              className="h-1 w-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 19l-7-7 7-7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <div className="swiper-button-next absolute  z-50 top-[100px]">
            <svg
              className="h-1 w-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 5l7 7-7 7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          {renderSlides()}
        </Swiper>
      </div>
    </div>
  );
};
